import React, { useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Image,
  ImageSourcePropType,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { trackTutorialEvent } from '../../utils/analytics';
import Button from '../common/Button';

export interface WelcomeModalProps {
  visible: boolean;
  onClose: () => void;
  onStartTour: () => void;
  onMaybeLater?: () => void;
  /** Optional illustration shown at the top. Pass require('./path.png') or { uri: '...' }. */
  imageSource?: ImageSourcePropType;
}

const defaultIllustration = require('../../../assets/illustrations/high-five.png');

const WelcomeModal: React.FC<WelcomeModalProps> = ({
  visible,
  onClose,
  onStartTour,
  onMaybeLater,
  imageSource = defaultIllustration,
}) => {
  const { colors } = useTheme();
  const windowWidth = Dimensions.get('window').width;
  const modalMaxWidth = Math.min(500, windowWidth * 0.9);

  useEffect(() => {
    if (visible) {
      trackTutorialEvent('modal_view');
    }
  }, [visible]);

  const handleClose = () => {
    trackTutorialEvent('modal_close');
    onClose();
  };

  const handleStartTour = () => {
    trackTutorialEvent('modal_start_tour');
    onStartTour();
  };

  const handleMaybeLater = () => {
    trackTutorialEvent('modal_maybe_later');
    (onMaybeLater ?? onClose)();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      statusBarTranslucent={Platform.OS !== 'web'}
    >
      <View
        style={[
          styles.overlay,
          Platform.OS === 'web' &&
            ({
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
            } as Record<string, unknown>),
        ]}
      >
        <View
          style={[
            styles.modalContainer,
            {
              backgroundColor: '#FFFFFF',
              maxWidth: modalMaxWidth,
              ...(Platform.OS === 'web' && {
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }),
            },
          ]}
        >
          <TouchableOpacity
            onPress={handleClose}
            style={styles.closeButton}
            hitSlop={{ top: 12, right: 12, bottom: 12, left: 12 }}
          >
            <Ionicons name="close" size={22} color="#1F2937" />
          </TouchableOpacity>

          {imageSource ? (
            <Image
              source={imageSource}
              style={[styles.illustrationWidth, styles.illustrationHeight]}
              resizeMode="contain"
              accessibilityLabel="Welcome illustration"
            />
          ) : (
            <View
              style={[
                styles.illustrationPlaceholder,
                { backgroundColor: colors.primary + '18' },
              ]}
            />
          )}

          <View style={styles.content}>
            <Text style={styles.headline}>Welcome to ColourSense!</Text>
            <Text
              style={[styles.body, { color: colors.secondary || '#6B7280' }]}
            >
              {
                "We're excited to have you here! ColourSense analyzes your skintone in real-time and there's so much more!"
              }
            </Text>

            <View style={styles.actions}>
              <Button
                title="Start the Tour"
                onPress={handleStartTour}
                variant="primary"
                style={styles.fullWidthButton}
              />
              <Button
                title="Maybe Later"
                onPress={handleMaybeLater}
                variant="outline"
                style={styles.fullWidthButton}
                hoverColor={colors.authBackground}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    width: '100%',
    borderRadius: 20,
    overflow: 'hidden',
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 12,
      elevation: 8,
    }),
    ...(Platform.OS === 'web' && {
      position: 'relative' as const,
      zIndex: 10001,
    }),
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
    padding: 4,
  },
  illustrationWidth: {
    width: '100%',
  },
  illustrationHeight: {
    height: 220,
    backgroundColor: '#FFFFFF',
  },
  illustrationPlaceholder: {
    width: '100%',
    height: 220,
  },
  content: {
    padding: 24,
    paddingTop: 20,
  },
  headline: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 12,
    lineHeight: 28,
    textAlign: 'center',
  },
  body: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 24,
  },
  actions: {
    flexDirection: 'column',
    gap: 12,
  },
  fullWidthButton: {
    width: '100%',
  },
});

export default WelcomeModal;
